import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { sendDirectSndManualTextBatch } from 'src/api/direct_snd';
import { sendEmailBatch } from 'src/api/email';
import { uploadMedia } from 'src/api/twilio';
import { useAuthContext } from 'src/auth/hooks';
import LoadingButton from 'src/components/Button';
import { useOrganizationContext } from 'src/context/organization';
import { useSnackbar } from 'src/components/snackbar';
import {
  MAX_SMS_MEDIA_BYTES,
  MAX_SMS_MEDIA_ERROR,
  TASK_BILLING_STATUS_CODES,
  TASK_BILLING_STATUS_ID_MAP,
  VALID_IMAGE_FILE_TYPES,
  VALID_VIDEO_FILE_TYPES,
} from 'src/constants/misc';
import { ORG_PHONE_PURPOSE_CODES } from 'src/constants/org-phones';
import { GET_ORGANIZATION_BY_ID } from 'src/graphql/organizations/queries';
import {
  TEXTING_TASK_STEP_EXECUTION_TYPES,
  TWILIO_MMS_CHARACTER_LIMIT,
  MMS_LIMIT_WARNING,
} from 'src/constants/task-types';
import { bulkSanitizeNumbers } from 'src/hooks/useTestMessage';
import { useParams } from 'src/routes/hook';
import { UploadFileButton } from 'src/sections/manager/tasks/components/task/sms-components';
import { usePhoneNumberSelector } from 'src/sections/superadmin/components/phone-numbers/usePhoneNumberSelector';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function QuickTextDialog({
  open,
  onClose,
  recipientLabel = '',
  numbers = [],
  emails = [],
  missingPhoneCount = 0,
  roleGroups,
}) {
  const { user } = useAuthContext();
  const { selectedOrganizationId } = useOrganizationContext();
  const { orgId: urlOrgId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [content, setContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);

  // When roleGroups is provided, the dialog renders a role multi-select and
  // derives the recipient numbers / label / skipped-count from the selection.
  // Otherwise it falls back to the numbers/recipientLabel/missingPhoneCount props.
  const hasRoleGroups = Array.isArray(roleGroups) && roleGroups.length > 0;
  const allRoleCodes = hasRoleGroups ? roleGroups.map((g) => g.code) : [];
  const [selectedRoles, setSelectedRoles] = useState(allRoleCodes);

  const selectedGroups = hasRoleGroups
    ? roleGroups.filter((g) => selectedRoles.includes(g.code))
    : [];
  const allSelected = hasRoleGroups && selectedRoles.length === allRoleCodes.length;

  const derivedNumbers = hasRoleGroups
    ? Array.from(new Set(selectedGroups.flatMap((g) => g.phones || [])))
    : numbers;
  const derivedEmails = hasRoleGroups
    ? Array.from(new Set(selectedGroups.flatMap((g) => g.emails || [])))
    : emails;
  const derivedMissingCount = hasRoleGroups
    ? selectedGroups.reduce((sum, g) => sum + (g.missingCount || 0), 0)
    : missingPhoneCount;
  const derivedLabel = hasRoleGroups
    ? allSelected
      ? 'All Users'
      : selectedGroups.map((g) => g.label).join(', ') || 'No one'
    : recipientLabel;

  // Reachable = distinct members with a phone OR an email (role groups are
  // disjoint by role code, so counts sum cleanly).
  const groupReach = (g) => g.count ?? Math.max((g.phones || []).length, (g.emails || []).length);
  const totalRecipients = hasRoleGroups ? roleGroups.reduce((sum, g) => sum + groupReach(g), 0) : 0;

  const toggleRole = (code) =>
    setSelectedRoles((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  const toggleAll = () =>
    setSelectedRoles((prev) => (prev.length === allRoleCodes.length ? [] : allRoleCodes));

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    if (
      !VALID_IMAGE_FILE_TYPES.includes(file.type) &&
      !VALID_VIDEO_FILE_TYPES.includes(file.type)
    ) {
      enqueueSnackbar('Invalid file type. Please upload an image or video.', { variant: 'error' });
      return;
    }
    if (file.size > MAX_SMS_MEDIA_BYTES) {
      enqueueSnackbar(MAX_SMS_MEDIA_ERROR, { variant: 'error' });
      return;
    }
    setSelectedFile(file);
  };

  const isSuperAdmin = user?.users_user_roles?.[0]?.user_roles_roles?.code === 'superadmin';
  // Managers message on behalf of the organization they have selected; superadmins, who
  // select no organization, on behalf of the one in the URL.
  const messageOrgId = isSuperAdmin ? Number(urlOrgId) || undefined : selectedOrganizationId;
  const { data: messageOrgData } = useQuery(GET_ORGANIZATION_BY_ID, {
    variables: { org_id: messageOrgId },
    skip: !messageOrgId,
  });

  const { RenderPhoneNumberSelector, orgPhoneNumber, orgId } = usePhoneNumberSelector({
    selectedOrganizationId: messageOrgId,
    // Quick text always sends via DirectSND, so only offer `direct_snd_sms` senders —
    // a Twilio `sms_text` number here would be handed to the wrong provider.
    purposeCodes: [ORG_PHONE_PURPOSE_CODES.DIRECT_SND_SMS_PURPOSE_CODE],
    autoSelectFirst: true,
    autoSelectForSelectedOrg: true,
    includeSmsSenderOrgNumbers: true,
  });

  const { valid: validNumbers } = bulkSanitizeNumbers((derivedNumbers || []).join(','));
  const validEmails = Array.from(
    new Set((derivedEmails || []).map((e) => (e || '').trim()).filter((e) => EMAIL_REGEX.test(e)))
  );

  // For non-superadmins, SMS is only usable when a sender org phone number is
  // available — when none is, the SMS checkbox is force-unchecked and disabled.
  // Superadmins can still pick a sender number, so it isn't force-disabled for
  // them (a missing number is caught at send time).
  const smsAvailable = isSuperAdmin || Boolean(orgPhoneNumber);
  const smsChecked = smsEnabled && smsAvailable;

  // Effective counts respect the channel toggles — used for the Send-button
  // enablement and the recipient summary.
  const smsCount = smsChecked ? validNumbers.length : 0;
  const emailCount = emailEnabled ? validEmails.length : 0;
  const willSendSms = smsChecked && validNumbers.length > 0;
  const willSendEmail = emailEnabled && validEmails.length > 0;
  const canSend = Boolean(content.trim()) && smsCount + emailCount > 0;

  const handleClose = () => {
    if (loading) return;
    setContent('');
    setSelectedFile(null);
    onClose();
  };

  const handleSend = async () => {
    if (!content.trim()) {
      enqueueSnackbar('Message content is required', { variant: 'error' });
      return;
    }
    if (!willSendSms && !willSendEmail) {
      enqueueSnackbar('No valid recipients for the selected delivery methods', {
        variant: 'error',
      });
      return;
    }
    if (willSendSms && !orgPhoneNumber) {
      enqueueSnackbar('Select a sender phone number first', { variant: 'error' });
      return;
    }
    if (willSendSms && selectedFile && content.length > TWILIO_MMS_CHARACTER_LIMIT) {
      enqueueSnackbar(MMS_LIMIT_WARNING, { variant: 'error' });
      return;
    }
    setLoading(true);
    try {
      let media_url;
      let media_mime_type;
      let media_file_name;
      let media_size;
      if (selectedFile) {
        const { file_name, mime_type, size, url } = await uploadMedia({ media_file: selectedFile });
        media_url = url;
        media_mime_type = mime_type;
        media_file_name = file_name;
        media_size = size;
      }

      const channels = [];
      if (willSendSms) {
        channels.push({
          label: 'SMS',
          promise: sendDirectSndManualTextBatch({
            orgId,
            userId: user.user_id,
            name: `Quick message to ${derivedLabel}`,
            content,
            from: orgPhoneNumber,
            numbers: validNumbers,
            media_url,
            media_mime_type,
            media_file_name,
            media_size,
            type: TEXTING_TASK_STEP_EXECUTION_TYPES.CREATE,
            task_billing_status_id: TASK_BILLING_STATUS_ID_MAP[TASK_BILLING_STATUS_CODES.REQUIRED],
          }),
        });
      }
      if (willSendEmail) {
        channels.push({
          label: 'Email',
          promise: sendEmailBatch({
            orgId,
            userId: user.user_id,
            name: `Quick message to ${derivedLabel}`,
            content,
            emails: validEmails,
            orgName: messageOrgData?.row?.name,
            media_url,
            media_file_name,
          }),
        });
      }

      const results = await Promise.allSettled(channels.map((c) => c.promise));
      const succeeded = channels.filter((_, i) => results[i].status === 'fulfilled');
      const failed = channels.filter((_, i) => results[i].status === 'rejected');
      const failureReasons = [];
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.log(`${channels[i].label} send failed`, r.reason);
          const reason = r.reason?.response?.data?.message;
          if (reason) failureReasons.push(reason);
        }
      });
      const failureDetail = failureReasons.length ? `: ${failureReasons.join(' ')}` : '';
      const emailResult = results[channels.findIndex((c) => c.label === 'Email')]?.value;
      const emailQueued = emailResult?.queued;
      const emailSkipped = emailResult?.skipped;
      const emailNote = `${
        emailQueued
          ? ` Email to ${emailQueued.toLocaleString()} ${
              emailQueued === 1 ? 'person' : 'people'
            } may take a few minutes to arrive.`
          : ''
      }${
        emailSkipped
          ? ` ${emailSkipped.toLocaleString()} ${
              emailSkipped === 1 ? 'person has' : 'people have'
            } unsubscribed from email and won't get it.`
          : ''
      }`;

      if (failed.length === 0) {
        enqueueSnackbar(
          `Message sent successfully via ${succeeded.map((c) => c.label).join(' & ')}!${emailNote}`,
          {
            variant: 'success',
          }
        );
        setContent('');
        setSelectedFile(null);
        onClose();
      } else if (succeeded.length > 0) {
        enqueueSnackbar(
          `Sent via ${succeeded.map((c) => c.label).join(' & ')}, but ${failed
            .map((c) => c.label)
            .join(' & ')} failed${failureDetail}`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(
          `Failed to send via ${failed.map((c) => c.label).join(' & ')}${failureDetail}`,
          {
            variant: 'error',
          }
        );
      }
    } catch (e) {
      console.log(e);
      enqueueSnackbar('Failed to send message', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Message {derivedLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {hasRoleGroups ? (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Recipients
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`All Users (${totalRecipients})`}
                  color={allSelected ? 'primary' : 'default'}
                  variant={allSelected ? 'filled' : 'outlined'}
                  onClick={toggleAll}
                />
                {roleGroups.map((g) => {
                  const count = groupReach(g);
                  const selected = selectedRoles.includes(g.code);
                  return (
                    <Chip
                      key={g.code}
                      label={`${g.label} (${count})`}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleRole(g.code)}
                      disabled={count === 0}
                    />
                  );
                })}
              </Stack>
            </Stack>
          ) : null}
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Delivery method
            </Typography>
            <FormGroup row>
              <Tooltip title={smsAvailable ? '' : 'Please reach out to setup Texting'}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={smsChecked}
                        disabled={!smsAvailable}
                        onChange={(e) => setSmsEnabled(e.target.checked)}
                      />
                    }
                    label={smsAvailable ? 'SMS' : 'SMS (no sender number)'}
                  />
                </span>
              </Tooltip>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emailEnabled}
                    onChange={(e) => setEmailEnabled(e.target.checked)}
                  />
                }
                label="Email"
              />
            </FormGroup>
          </Stack>
          {smsChecked ? <RenderPhoneNumberSelector title="Sender phone number" mb={0} /> : null}
          <Typography variant="body2" color="text.secondary">
            {[
              smsChecked
                ? `SMS: ${validNumbers.length} number${validNumbers.length === 1 ? '' : 's'}`
                : null,
              emailEnabled
                ? `Email: ${validEmails.length} email${validEmails.length === 1 ? '' : 's'}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'No delivery method selected'}
            {smsChecked && derivedMissingCount > 0
              ? ` — ${derivedMissingCount} skipped (no phone number)`
              : ''}
          </Typography>
          {smsCount + emailCount === 0 ? (
            <Alert severity="warning">No valid recipients for the selected delivery methods.</Alert>
          ) : null}
          <TextField
            label="Message"
            multiline
            minRows={4}
            fullWidth
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <UploadFileButton
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            handleFileSelect={handleFileSelect}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton onClick={handleSend} loading={loading} disabled={!canSend}>
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
